import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Suspense, lazy, useEffect } from "react";
import { CartProvider } from "./lib/cart";
import { AuthProvider } from "./lib/auth";
import { installClientErrorReporter } from "./lib/errorReporter";
import { CompareProvider } from "./lib/compare";
import CompareTray from "./components/CompareTray";
import MobileStickyCTA from "./components/MobileStickyCTA";
import CookieConsent from "./components/CookieConsent";
import ExitIntentPopup from "./components/ExitIntentPopup";
import CartRecoveryBanner from "./components/CartRecoveryBanner";
import CartRestoredToast from "./components/CartRestoredToast";
import AbandonedCartTracker from "./components/AbandonedCartTracker";
import TidioLoader from "./components/TidioLoader";
import MetaPixelLoader from "./components/MetaPixelLoader";
import GoogleAdsLoader from "./components/GoogleAdsLoader";
import { Toaster } from "sonner";
import NewsletterPopup from "./components/NewsletterPopup";
import usePageTracking from "./lib/pageTracking";
import { useLocation } from "react-router-dom";
// Landing is the LCP page → keep eager-imported to avoid a flash of empty
// content. Everything else is lazy-loaded → smaller initial bundle, faster
// Largest Contentful Paint, lower Interaction to Next Paint.
import Landing from "./pages/Landing";
const CustomizeLanding = lazy(() => import("./pages/CustomizeLanding"));
const CategoriesIndex = lazy(() => import("./pages/CategoriesIndex"));
const Customizer = lazy(() => import("./pages/Customizer"));
const ReadyMadeShop = lazy(() => import("./pages/ReadyMadeShop"));
const ReadyMadeProduct = lazy(() => import("./pages/ReadyMadeProduct"));
const Cart = lazy(() => import("./pages/Cart"));
const ResumeCart = lazy(() => import("./pages/ResumeCart"));
const Checkout = lazy(() => import("./pages/Checkout"));
const Success = lazy(() => import("./pages/Success"));
import Admin from "./pages/Admin";
const BulkOrders = lazy(() => import("./pages/BulkOrders"));
const Gallery = lazy(() => import("./pages/Gallery"));
const GalleryDesigns = lazy(() => import("./pages/GalleryDesigns"));
const InvoicePublic = lazy(() => import("./pages/InvoicePublic"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const FAQ = lazy(() => import("./pages/FAQ"));
const RequestQuote = lazy(() => import("./pages/RequestQuote"));
const Policy = lazy(() => import("./pages/Policy"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const CityServiceLanding = lazy(() => import("./pages/CityServiceLanding"));
const ServiceAreas = lazy(() => import("./pages/ServiceAreas"));
const Login = lazy(() => import("./pages/Login"));
const Signup = lazy(() => import("./pages/Signup"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const Account = lazy(() => import("./pages/Account"));
const AccountInvoices = lazy(() => import("./pages/AccountInvoices"));
const TrackOrder = lazy(() => import("./pages/TrackOrder"));
const ForgotPassword = lazy(() => import("./pages/Password").then((m) => ({ default: m.ForgotPassword })));
const ResetPassword = lazy(() => import("./pages/Password").then((m) => ({ default: m.ResetPassword })));
const CityLanding = lazy(() => import("./pages/CityLanding"));
const SharedCart = lazy(() => import("./pages/SharedCart"));
const DtfLanding = lazy(() => import("./pages/DtfLanding"));
const DtfPrintBySize = lazy(() => import("./pages/DtfPrintBySize"));
const DtfPrintByRoll = lazy(() => import("./pages/DtfPrintByRoll"));
const Comparison = lazy(() => import("./pages/Comparison"));
import "./App.css";

const RouteFallback = () => (
  <div className="min-h-screen bg-black flex items-center justify-center text-zinc-600 font-mono text-xs tracking-widest uppercase">
    Loading…
  </div>
);

/**
 * Captures `?promo=CODE` from any URL and stashes it in localStorage so the
 * Cart page can auto-apply it later even after a user navigates through
 * /customize → product → /cart. Set by the PostPurchaseUpsell modal.
 */
function usePromoCapture() {
  const location = useLocation();
  useEffect(() => {
    try {
      const sp = new URLSearchParams(location.search);
      const promo = (sp.get("promo") || "").trim().toUpperCase();
      if (promo) localStorage.setItem("tc_pending_promo", promo);
    } catch { /* ignore */ }
  }, [location.search]);
}

/**
 * SEO — normalize trailing slashes to the non-slash canonical.
 * Google, Bing and AI answer-engines treat /blog/ and /blog as duplicate
 * content unless a canonical link consolidates them. We already emit
 * canonical links, but this client-side rewrite kills the duplicate
 * signal entirely by pushing the browser (and Googlebot, which runs JS)
 * to the canonical URL immediately on load.
 *
 * Skipped for the site root ("/"), because "/" IS the canonical.
 */
function useTrailingSlashRedirect() {
  const location = useLocation();
  useEffect(() => {
    const p = location.pathname;
    if (p !== "/" && p.endsWith("/")) {
      const target = p.slice(0, -1) + location.search + location.hash;
      window.history.replaceState(null, "", target);
    }
  }, [location.pathname, location.search, location.hash]);
}

// Inner router shell so `usePageTracking` can use `useLocation` — that hook
// only works inside <BrowserRouter />.
function AppRoutes() {
  usePageTracking();
  usePromoCapture();
  useTrailingSlashRedirect();
  return (
    <>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/customize" element={<CustomizeLanding />} />
          <Route path="/customize/categories" element={<CategoriesIndex scope="customize" />} />
          <Route path="/customize/category/:category" element={<CustomizeLanding />} />
          <Route path="/customize/category/:category/:subcategory" element={<CustomizeLanding />} />
          <Route path="/customize/:productId" element={<Customizer />} />
          <Route path="/ready-made" element={<ReadyMadeShop />} />
          <Route path="/ready-made/categories" element={<CategoriesIndex scope="ready-made" />} />
          <Route path="/ready-made/category/:category" element={<ReadyMadeShop />} />
          <Route path="/ready-made/category/:category/:subcategory" element={<ReadyMadeShop />} />
          <Route path="/ready-made/:category" element={<ReadyMadeShop />} />
          <Route path="/design/:id" element={<ReadyMadeProduct />} />
          <Route path="/dtf" element={<DtfLanding />} />
          <Route path="/dtf/print-by-size" element={<DtfPrintBySize />} />
          <Route path="/dtf/print-by-roll" element={<DtfPrintByRoll />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/resume-cart/:token" element={<ResumeCart />} />
          <Route path="/shared-cart/:token" element={<SharedCart />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/success" element={<Success />} />
          <Route path="/bulk-orders" element={<BulkOrders />} />
          <Route path="/quote" element={<RequestQuote />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/gallery/designs" element={<GalleryDesigns />} />
          <Route path="/invoice/:token" element={<InvoicePublic />} />
          <Route path="/about" element={<About />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/policy/:slug" element={<Policy />} />
          {/* P0-2: clean top-level routes for footer SUPPORT + nav links so
              none render blank. /policy/:slug kept for backwards-compat. */}
          <Route path="/shipping" element={<Policy slug="shipping" />} />
          <Route path="/refunds" element={<Policy slug="refund" />} />
          <Route path="/refund" element={<Policy slug="refund" />} />
          <Route path="/privacy" element={<Policy slug="privacy" />} />
          <Route path="/terms" element={<Policy slug="terms" />} />
          <Route path="/dtf-transfers" element={<DtfLanding />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/account" element={<Account />} />
          <Route path="/account/invoices" element={<AccountInvoices />} />
          <Route path="/track-order" element={<TrackOrder />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/toronto" element={<CityLanding />} />
          <Route path="/mississauga" element={<CityLanding />} />
          <Route path="/gta" element={<CityLanding />} />
          <Route path="/service-areas" element={<ServiceAreas />} />
          <Route path="/compare/:slug" element={<Comparison />} />
          {(() => {
            const services = ["custom-tshirts", "custom-hoodies", "dtf-printing", "vinyl-printing", "bulk-printing", "custom-polos", "custom-hats", "embroidery", "team-uniforms"];
            const cities = ["toronto", "mississauga", "brampton", "scarborough", "etobicoke",
                            "vaughan", "markham", "north-york", "oakville", "richmond-hill",
                            "burlington", "ajax", "pickering", "whitby",
                            "hamilton", "milton", "caledon", "newmarket", "aurora",
                            "halton-hills", "georgetown", "stouffville"];
            const routes = [];
            for (const s of services) for (const c of cities) {
              routes.push(<Route key={`${s}-${c}`} path={`/${s}-${c}`} element={<CityServiceLanding />} />);
            }
            return routes;
          })()}
          {/* Catch-all: never render a blank page for an unknown route. */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      <CompareTray />
      <MobileStickyCTA />
      <ExitIntentPopup />
      <CartRecoveryBanner />
      <CartRestoredToast />
      <AbandonedCartTracker />
      <TidioLoader />
      <MetaPixelLoader />
      <GoogleAdsLoader />
      {/* Global toast portal — Pages that mount their own <Toaster /> (Cart,
          Admin, Account) will deduplicate via sonner's singleton. Mounted
          here so cart-restored toasts fire from Landing / Customizer too. */}
      <Toaster
        richColors
        position="top-right"
        duration={5000}
        closeButton
        toastOptions={{
          "data-testid": "toaster-portal",
          // Inline-styled close button so it's visually consistent with
          // the rest of the brand — sonner's default close is a tiny
          // circle on the left; we want a top-right X.
          classNames: {
            closeButton:
              "!absolute !top-2 !right-2 !left-auto !bg-transparent " +
              "!border-0 !text-zinc-500 hover:!text-white !shadow-none " +
              "!w-6 !h-6 !rounded-none",
          },
        }}
      />
      <NewsletterPopup />
      <CookieConsent />
    </>
  );
}

export default function App() {
  // iter92 — install the admin-only client-error reporter once at boot.
  useEffect(() => { installClientErrorReporter(); }, []);
  return (
    <HelmetProvider>
      <AuthProvider>
        <CartProvider>
          <CompareProvider>
            <BrowserRouter>
              <AppRoutes />
            </BrowserRouter>
          </CompareProvider>
        </CartProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}
